<script setup lang="ts">
import { onActivated, onDeactivated, onMounted, ref, watch } from 'vue'
import { api, ApiError } from '../api/client'
import type { CatalogCategory, CatalogSeries, CatalogSync } from '../api/types'
import PageBody from '../components/PageBody.vue'
import PageHeader from '../components/PageHeader.vue'
import { Button, EmptyState, Panel } from '../components/ui'
import { formatDateTime } from '../lib/format'
import { useTitle } from '../lib/hooks'

defineOptions({ name: 'Catalog' })

const STATUS: Record<string, string> = {
  idle: '尚未同步',
  running: '同步中',
  ok: '已完成',
  failed: '失败',
}

useTitle('目录')
const data = ref<CatalogSync | null>(null)
const error = ref('')
const busy = ref(false)
const categories = ref<CatalogCategory[]>([])
const series = ref<CatalogSeries[]>([])
const categoryID = ref('0')
const previewError = ref('')
const previewLoading = ref(false)
let timer = 0
let loaded = false
let seriesSeq = 0

async function load() {
  try {
    data.value = await api.catalogStatus()
    error.value = ''
  } catch (err) {
    error.value = err instanceof ApiError ? err.message : '加载失败'
  }
}

async function loadSeries(id: string) {
  const seq = ++seriesSeq
  previewLoading.value = true
  try {
    const list = await api.catalogSeries(id)
    if (seq !== seriesSeq) return
    series.value = list.series
    previewError.value = ''
  } catch (err) {
    if (seq !== seriesSeq) return
    series.value = []
    previewError.value = err instanceof ApiError ? err.message : '目录加载失败'
  } finally {
    if (seq === seriesSeq) previewLoading.value = false
  }
}

async function loadPreview() {
  try {
    const cats = await api.catalogCategories()
    categories.value = cats.categories
    if (!categories.value.some((c) => c.id === categoryID.value)) {
      categoryID.value = categories.value[0]?.id ?? '0'
    }
    if (categories.value.length === 0) {
      seriesSeq++
      series.value = []
      previewError.value = ''
      previewLoading.value = false
      return
    }
    await loadSeries(categoryID.value)
  } catch (err) {
    previewError.value = err instanceof ApiError ? err.message : '目录加载失败'
  }
}

function selectCategory(id: string) {
  if (id === categoryID.value) return
  categoryID.value = id
  series.value = []
  previewError.value = ''
  void loadSeries(id)
}

async function sync() {
  if (busy.value || data.value?.status === 'running') return
  busy.value = true
  try {
    data.value = await api.syncCatalog()
    error.value = ''
  } catch (err) {
    error.value = err instanceof ApiError ? err.message : '同步失败'
  } finally {
    busy.value = false
  }
}

function categoryName(id?: string) {
  return categories.value.find((c) => c.id === id)?.name ?? id ?? '—'
}

function startPoll() {
  if (timer) return
  timer = window.setInterval(() => {
    if (data.value?.status === 'running') void load()
  }, 2000)
}

function stopPoll() {
  if (!timer) return
  window.clearInterval(timer)
  timer = 0
}

watch(
  () => data.value?.status,
  (status, prev) => {
    if (prev === 'running' && status && status !== 'running') void loadPreview()
  },
)

onMounted(() => {
  if (!loaded) {
    loaded = true
    void load()
    void loadPreview()
  }
  startPoll()
})

onActivated(() => startPoll())
onDeactivated(() => stopPoll())
</script>

<template>
  <PageHeader title="目录">
    <template #actions>
      <Button
        size="md"
        variant="primary"
        :loading="busy || data?.status === 'running'"
        :disabled="data?.configured === false"
        @click="sync"
      >
        同步
      </Button>
    </template>
  </PageHeader>

  <PageBody>
    <Panel title="同步状态">
      <p v-if="error" class="px-4 py-4 text-[14px] text-danger">{{ error }}</p>
      <dl v-else-if="data" class="divide-y divide-line">
        <div class="flex items-start justify-between gap-4 px-4 py-3">
          <dt class="text-[13px] text-muted">状态</dt>
          <dd class="text-[14px]">{{ STATUS[data.status] ?? data.status }}</dd>
        </div>
        <div class="flex items-start justify-between gap-4 px-4 py-3">
          <dt class="text-[13px] text-muted">上次成功</dt>
          <dd class="text-[14px]">{{ formatDateTime(data.synced_at) }}</dd>
        </div>
        <div class="flex items-start justify-between gap-4 px-4 py-3">
          <dt class="text-[13px] text-muted">分类</dt>
          <dd class="text-[14px]">{{ data.category_count }}</dd>
        </div>
        <div class="flex items-start justify-between gap-4 px-4 py-3">
          <dt class="text-[13px] text-muted">剧集</dt>
          <dd class="text-[14px]">{{ data.series_count }}</dd>
        </div>
        <div v-if="data.error" class="flex items-start justify-between gap-4 px-4 py-3">
          <dt class="text-[13px] text-muted">结果</dt>
          <dd class="text-[14px] text-danger">{{ data.error }}</dd>
        </div>
        <p v-if="!data.configured" class="px-4 py-3 text-[13px] text-muted">
          未配置面板线路账号，无法同步。
        </p>
      </dl>
    </Panel>

    <Panel class="mt-4" title="内容预览" :hint="series.length ? `${series.length} 部` : undefined">
      <p v-if="previewError && categories.length === 0" class="px-4 py-4 text-[14px] text-danger">
        {{ previewError }}
      </p>
      <EmptyState v-else-if="categories.length === 0" title="还没有目录" hint="点右上角同步后再来看。" />
      <template v-else>
        <div class="flex flex-wrap gap-2 border-b border-line px-4 py-3">
          <button
            v-for="cat in categories"
            :key="cat.id"
            type="button"
            :class="
              cat.id === categoryID
                ? 'border-accent bg-accent/12 text-accent-dim'
                : 'border-line bg-panel text-muted hover:bg-hover'
            "
            class="rounded-md border px-2.5 py-1 text-[13px] font-medium"
            @click="selectCategory(cat.id)"
          >
            {{ cat.name }}
          </button>
        </div>
        <p v-if="previewError" class="px-4 py-4 text-[14px] text-danger">{{ previewError }}</p>
        <EmptyState v-else-if="!previewLoading && series.length === 0" title="这个分类下没有剧" />
        <ul v-else class="divide-y divide-line">
          <li v-for="item in series" :key="item.id" class="flex items-start gap-4 px-4 py-4">
            <RouterLink
              :to="`/catalog/${encodeURIComponent(item.id)}`"
              class="block w-[108px] shrink-0 overflow-hidden rounded-md bg-raised ring-1 ring-line transition hover:ring-accent sm:w-[120px]"
            >
              <img
                v-if="item.cover_url"
                :src="item.cover_url"
                :alt="item.title"
                class="aspect-[2/3] w-full object-cover"
              />
              <span v-else class="block aspect-[2/3] w-full bg-raised" />
            </RouterLink>
            <span class="min-w-0 pt-0.5">
              <RouterLink
                :to="`/catalog/${encodeURIComponent(item.id)}`"
                class="block truncate text-[15px] font-medium text-text hover:text-accent-dim"
              >
                {{ item.title }}
              </RouterLink>
              <span class="mt-1 block truncate text-[12.5px] text-dim">
                {{ categoryName(item.category_id) }} · {{ item.id }}
              </span>
              <span v-if="item.plot" class="mt-2 line-clamp-2 text-[13px] leading-relaxed text-muted">
                {{ item.plot }}
              </span>
            </span>
          </li>
        </ul>
      </template>
    </Panel>
  </PageBody>
</template>
